import { Show } from "@refinedev/antd";
import {
  Typography,
  Card,
  Row,
  Col,
  Space,
  Flex,
  Divider,
  Button,
  Tag,
  Modal,
  Input,
  message,
} from "antd";
import { useShow, useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { ReloadOutlined, CopyOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { axiosInstance } from "../../authProvider";

const { Title, Text } = Typography;

export const ClientShow = () => {
  const { queryResult } = useShow();
  const { data, isLoading, refetch } = queryResult;
  const _t = useTranslate();

  const location = useLocation();
  const locationState = location.state as { initialSecret?: string } | null;

  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const [initialSecret, setInitialSecret] = useState<string | null>(
    locationState?.initialSecret ?? null,
  );
  const [initialSecretModalOpen, setInitialSecretModalOpen] = useState(
    !!locationState?.initialSecret,
  );

  useEffect(() => {
    if (locationState?.initialSecret) {
      setInitialSecret(locationState.initialSecret);
      setInitialSecretModalOpen(true);
    }
  }, [locationState?.initialSecret]);

  const record = data?.data;
  const clientId = record?.clientId;

  const handleRotateSecret = () => {
    if (!clientId) return;
    setRotating(true);
    axiosInstance
      .post(`/clients/${clientId}/rotate-secret`)
      .then((res) => {
        const secret = res.data?.secret ?? null;
        setNewSecret(secret);
        setRotateModalOpen(true);
        refetch();
      })
      .catch(() => {
        message.error(_t("clients.messages.rotateError"));
      })
      .finally(() => setRotating(false));
  };

  const copySecret = (secret?: string | null) => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      message.success(_t("clients.messages.copied"));
    }
  };

  const copyClientId = () => {
    if (clientId) {
      navigator.clipboard.writeText(clientId);
      message.success(_t("clients.messages.copied"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Show isLoading={isLoading}>
        <Space direction="vertical" size="large" className="w-full">
          <Space direction="vertical" size="small" className="w-full">
            <Title level={2}>{record?.name}</Title>
            <Space>
              <Text type="secondary" className="font-mono">
                {record?.clientId}
              </Text>
              {record?.clientId && (
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={copyClientId}
                />
              )}
            </Space>
          </Space>

          <Card title={_t("clients.fields.credentials")} size="small">
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={12}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("clients.fields.clientId")}</Text>
                  <Space>
                    <Text strong className="font-mono">
                      {record?.clientId}
                    </Text>
                    {record?.clientId && (
                      <Button
                        size="small"
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={copyClientId}
                      />
                    )}
                  </Space>
                </Space>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("clients.fields.secret")}</Text>
                  <Text strong className="font-mono">
                    {record?.secret ? "••••••••" : "—"}
                  </Text>
                </Space>
              </Col>
              <Col xs={24}>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={rotating}
                  onClick={handleRotateSecret}
                >
                  {_t("clients.actions.rotateSecret")}
                </Button>
              </Col>
            </Row>
          </Card>

          <Card title={_t("clients.fields.details")} size="small">
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("clients.fields.scopes")}</Text>
                  <Space size={[0, 4]} wrap>
                    {Array.isArray(record?.scopes) &&
                      record.scopes.map((s: string) => (
                        <Tag key={s} color="blue">
                          {s}
                        </Tag>
                      ))}
                  </Space>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("clients.fields.isActive")}</Text>
                  <Tag color={record?.isActive ? "green" : "red"}>
                    {record?.isActive ? _t("titles.yes") : _t("titles.no")}
                  </Tag>
                </Space>
              </Col>
            </Row>
          </Card>

          <Divider className="mt-4 mb-0" />
          <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
            <Col xs={12} sm={8} md={6}>
              <Flex gap="middle">
                <Text strong type="secondary">
                  {_t("clients.fields.createdAt")}{" "}
                </Text>
                <DateField
                  value={record?.createdAt}
                  type="secondary"
                  format="DD/MM/YYYY HH:mm"
                />
              </Flex>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Flex gap="middle">
                <Text strong type="secondary">
                  {_t("clients.fields.updatedAt")}{" "}
                </Text>
                <DateField
                  value={record?.updatedAt}
                  type="secondary"
                  format="DD/MM/YYYY HH:mm"
                />
              </Flex>
            </Col>
          </Row>
        </Space>
      </Show>

      <Modal
        title={_t("clients.messages.rotateSuccessTitle")}
        open={rotateModalOpen}
        onCancel={() => {
          setRotateModalOpen(false);
          setNewSecret(null);
        }}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => copySecret(newSecret)}
          >
            {_t("clients.actions.copySecret")}
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setRotateModalOpen(false);
              setNewSecret(null);
            }}
          >
            {_t("buttons.close")}
          </Button>,
        ]}
      >
        <p className="mb-2">{_t("clients.messages.rotateSuccessBody")}</p>
        <Input.Password
          readOnly
          value={newSecret ?? ""}
          className="font-mono"
          addonAfter={
            <Button
              type="link"
              size="small"
              onClick={() => copySecret(newSecret)}
            >
              <CopyOutlined />
            </Button>
          }
        />
      </Modal>

      <Modal
        title={_t("clients.messages.rotateSuccessTitle")}
        open={initialSecretModalOpen}
        onCancel={() => {
          setInitialSecretModalOpen(false);
          setInitialSecret(null);
        }}
        footer={[
          <Button
            key="copy-initial"
            icon={<CopyOutlined />}
            onClick={() => copySecret(initialSecret)}
          >
            {_t("clients.actions.copySecret")}
          </Button>,
          <Button
            key="close-initial"
            type="primary"
            onClick={() => {
              setInitialSecretModalOpen(false);
              setInitialSecret(null);
            }}
          >
            {_t("buttons.close")}
          </Button>,
        ]}
      >
        <p className="mb-2">{_t("clients.messages.rotateSuccessBody")}</p>
        <Input.Password
          readOnly
          value={initialSecret ?? ""}
          className="font-mono"
          addonAfter={
            <Button
              type="link"
              size="small"
              onClick={() => copySecret(initialSecret)}
            >
              <CopyOutlined />
            </Button>
          }
        />
      </Modal>
    </div>
  );
};
